// Project lists and foreman→project assignments live in the database
// (projects + foreman_projects, loaded by auth.js loadUserProjects).

// The core list is trade-neutral (roadmap §4b). Entries tagged pack:'…' only
// appear for orgs that opted into that trade pack (visibleSubmissionTypes /
// window.Ordinex.packs.visibleTypes decide, per render).
const SUBMISSION_TYPES = [
  { id: 'Daily Tailgate',              icon: '☀️',  desc: 'Morning safety meeting' },
  { id: 'Weekly Toolbox Talk',         icon: '🗣️',  desc: 'Weekly safety topic discussion' },
  { id: 'Incident Report',             icon: '🚨',  desc: 'Injury or near miss' },
  { id: 'Telehandler Inspection',      icon: '🏗️',  desc: 'Pre-use telehandler checklist' },
  { id: 'Forklift Inspection',         icon: '🚜',  desc: 'Pre-use forklift checklist' },
  { id: 'E-Pallet Jack Inspection',    icon: '🔋',  desc: 'Electric pallet jack checklist' },
  { id: 'Scaffolding Inspection',      icon: '🪜',  desc: 'Scaffold safety checklist' },
  { id: 'Safety Inspection',           icon: '🔍',  desc: 'General site safety inspection' },
  // 2026-09-03 (Renan): ONE QAQC form. The staged ITP sheet (0 filings ever)
  // was removed; this one is the walkthrough — contractor rep + GC sign the
  // acceptance in the app. Old id kept: pack overlays and the PDF layout key
  // on it, and two 2026-07 filings live under it.
  { id: 'QAQC - Foreman',              icon: '✅',  desc: 'Walkthrough with the GC — acceptance sign-off' },
  { id: 'Site Photos Only',            icon: '📷',  desc: 'Progress or documentation' },
  { id: 'Weekly Timesheet',            icon: '🕐',  desc: 'Weekly hours submission' },
  // NEUTRAL on purpose. Every trade reports units placed per day — squares of
  // membrane, feet of conduit, fixtures set. What differs is the item list,
  // and that is already per-org (material_types, seeded by the trade pack and
  // editable in Rates & Defaults). This form was masonry-only by accident of
  // where it was born: the TM app, where the only unit that existed was block.
  { id: 'Production Report',           icon: '📊',  desc: 'Weekly units placed, by day' },
  { id: 'Extra Work Order',            icon: '💼',  desc: 'Extra work to bill the GC' },
  { id: 'Hazard Observation',          icon: '⚠️',  desc: 'Unsafe condition found' },
  // Hot work is cross-trade (roofing torches, mechanical brazes, ironwork
  // welds), so this is NEUTRAL — packs that never light a flame hide it via
  // hidden_neutral_forms. It shipped pack-locked to roofing on 2026-07-28 and
  // was neutralized the same day, once mechanical needed the same record.
  { id: 'Hot Work & Fire Watch Record', icon: '🔥', desc: 'Cutting, welding, torch & fire watch log' },
  // Heavy equipment is the same machine in every trade that runs one, and the
  // form was written generic (equipment_type select) from day one. It shipped
  // pack-locked to sitework on 2026-07-28 and was neutralized the same day,
  // once demolition needed it — packs that run no heavy iron hide it.
  { id: 'Heavy Equipment Daily Log',   icon: '🚛',  desc: 'Pre-op check — excavators, dozers, rollers' },
  // MEWPs are the one machine nearly every interior trade runs, and the
  // pre-use inspection is required each shift (CSA B354). Neutral from birth
  // — the gap showed up in electrical, mechanical, GC and demolition before
  // this form existed. Packs that never rent a lift hide it.
  { id: 'MEWP Daily Log',              icon: '🛗',  desc: 'Pre-use check — scissor & boom lifts' },

  // Six records every construction company keeps, no matter the trade. They
  // shipped inside the drilling pack (2026-07-27) because that pack was
  // written first; neutralized 2026-07-28 so the other ten packs — and orgs
  // with no pack at all — stop going without them.
  { id: 'Daily Site Report',           icon: '📝',  desc: 'Crew, weather, work & production' },
  { id: 'Truck Monthly Inspection',    icon: '🚚',  desc: 'Monthly vehicle condition record' },
  { id: 'Site Orientation Record',     icon: '🧭',  desc: 'New worker site orientation' },
  { id: 'First Aid Assessment',        icon: '🩹',  desc: 'Attendant treatment record' },
  { id: 'Incident Investigation',      icon: '🕵️', desc: 'Incident / near miss investigation' },
  { id: 'Equipment Maintenance Record',icon: '🔧',  desc: 'Service & repair history per unit' },
  { id: 'Disciplinary Report',         icon: '⚖️',  desc: 'Conduct & safety violations record' },

  // ── Drilling & Shotcrete pack forms (docs/trade-packs/drilling-shotcrete/) ──
  { id: 'Drill Rig Daily Log',         icon: '⛏️',  desc: 'Pre-op check — track / hydraulic drills', pack: 'drilling_shotcrete' },
  { id: 'Air Compressor Daily Log',    icon: '💨',  desc: 'Pre-op check — portable compressors',     pack: 'drilling_shotcrete' },
  { id: 'Concrete Pump Daily Log',     icon: '🌀',  desc: 'Pre-op check — wet-mix pump & lines',     pack: 'drilling_shotcrete' },
  { id: 'Dry-Mix Pump Daily Log',      icon: '⚙️',  desc: 'Pre-op check — dry-process gun',          pack: 'drilling_shotcrete' },
  { id: 'Grout Plant Daily Log',       icon: '🥣',  desc: 'Pre-op check — mixer & grout pump',       pack: 'drilling_shotcrete' },
  { id: 'Anchor Installation Log',     icon: '⚓',  desc: 'Per-hole drilling & grouting record',     pack: 'drilling_shotcrete' },
  { id: 'Anchor Test Record',          icon: '🏋️', desc: 'Proof / performance / lift-off tests',    pack: 'drilling_shotcrete' },
  { id: 'Shotcrete Placement Record',  icon: '💦',  desc: 'Per-panel placement & cure record',       pack: 'drilling_shotcrete' },
  { id: 'Shotcrete Test Panel Log',    icon: '🧪',  desc: 'Test panels & core results',              pack: 'drilling_shotcrete' },
  { id: 'Grout Batch Log',             icon: '🧾',  desc: 'Grout batches & cube strengths',          pack: 'drilling_shotcrete' },

  // ── Excavation & Sitework pack form (docs/trade-packs/sitework/) ────────────
  { id: 'Excavation Daily Inspection', icon: '🕳️', desc: 'Competent person check before entry',     pack: 'sitework' },

  // ── Mechanical pack forms (docs/trade-packs/mechanical/) ────────────────────
  { id: 'Pressure Test Record',        icon: '📈',  desc: 'Piping test held & witnessed',            pack: 'mechanical' },
  { id: 'Refrigerant Service & Leak Log', icon: '❄️', desc: 'Charge, recovery & leak checks',        pack: 'mechanical' },

  // ── Demolition & Environmental pack forms (docs/trade-packs/demolition/) ────
  { id: 'Pre-Demolition Readiness Check', icon: '🏚️', desc: 'Survey, utilities & go / no-go',       pack: 'demolition' },
  { id: 'Abatement Containment Daily Log', icon: '☣️', desc: 'Enclosure & negative pressure per shift', pack: 'demolition' }
];

const TYPE_TO_FOLDER = {
  'Daily Tailgate':            'Daily Tailgates',
  'Weekly Toolbox Talk':       'Toolbox Talks',
  'Incident Report':           'Incident Reports',
  'Telehandler Inspection':    'Equipment Inspections',
  'Forklift Inspection':       'Equipment Inspections',
  'E-Pallet Jack Inspection':  'Equipment Inspections',
  'Scaffolding Inspection':    'Scaffolding Inspections',
  'Safety Inspection':         'Safety Inspections',
  'QAQC - Foreman':            'QAQC',
  'Site Photos Only':          'Site Photos',
  'Weekly Timesheet':          'Timesheets',
  'Production Report':         'Production Reports',
  'Extra Work Order':          'Extra Work Orders',
  'Hazard Observation':        'Hazard Observations',
  'Drill Rig Daily Log':          'Equipment Logs',
  'Air Compressor Daily Log':     'Equipment Logs',
  'Concrete Pump Daily Log':      'Equipment Logs',
  'Dry-Mix Pump Daily Log':       'Equipment Logs',
  'Grout Plant Daily Log':        'Equipment Logs',
  'Truck Monthly Inspection':     'Vehicle Inspections',
  'Equipment Maintenance Record': 'Equipment Maintenance',
  'Incident Investigation':       'Incident Reports',
  'First Aid Assessment':         'First Aid Records',
  'Disciplinary Report':          'Disciplinary Reports',
  'Site Orientation Record':      'Site Orientations',
  'Daily Site Report':            'Daily Site Reports',
  'Anchor Installation Log':      'Drilling Logs',
  'Anchor Test Record':           'Drilling Logs',
  'Shotcrete Placement Record':   'Shotcrete Records',
  'Shotcrete Test Panel Log':     'Shotcrete Records',
  'Grout Batch Log':              'Grout Records'
};

// ── TAILGATE ITEMS REVIEWED ───────────────────────────────────────────────────
const TAILGATE_ITEMS = {
  'Permits & PPE': [
    'Hot Work Permit',
    'Confined Space Entry',
    'Lock Out / Tag Out',
    'Task-Appropriate Gloves',
    'Eye Protection (safety glasses)',
    'Respirator / Dust Mask',
    'Hard Hat & Safety Boots'
  ],
  'Work Surfaces & Equipment': [
    'Uneven or Slippery Surfaces',
    'Mobile Equipment in Area',
    'Spotters Required',
    'Telehandler / Forklift Inspected',
    'Ladders in Use & Secured',
    'Material Storage & Securement',
    'Scaffold Inspected & Tagged'
  ],
  'Site Conditions': [
    'Dust / Silica Exposure (cutting, grinding)',
    'Manual Material Handling',
    'Sharp Edges & Protruding Objects',
    'Overhead Hazards',
    'Adverse Weather Conditions',
    'Housekeeping Maintained'
  ],
  'Fall Protection': [
    'Guardrails in Place',
    'Toe Boards in Place',
    'Full Body Harness Required',
    'Lanyard / Retractable',
    'Daily Inspections',
    'Fall Restraint System'
  ]
};

// ── DEFAULT FLRA TASKS (trade-neutral fallback; an org/pack schema row's
// meta.flra.tasks, resolved via metaFor, wins when present — see js/forms/
// schemas.js). preselected:true = included by default on the Daily Tailgate.
const DEFAULT_FLRA_TASKS = [
  {
    task: 'Material handling / Moving materials on site',
    hazards: ['Working with Hand Tools', 'Slips / Trips Possible', 'Pinch Points'],
    riskLevel: 'Low',
    controls: 'Proper lifting techniques, team lift for heavy items, clear travel paths, gloves',
    preselected: true
  },
  {
    task: 'Working with power tools',
    hazards: ['Working with Power Tools', 'Exposure to Dust', 'Noise Exposure'],
    riskLevel: 'Medium',
    controls: 'Guards in place, inspect tools before use, safety glasses, hearing protection, dust control',
    preselected: true
  },
  {
    task: 'Working with hand tools',
    hazards: ['Working with Hand Tools', 'Pinch Points'],
    riskLevel: 'Low',
    controls: 'Right tool for the job, inspect condition before use, gloves, watch hand placement'
  },
  {
    task: 'Operating forklift / Material handling equipment',
    hazards: ['Operating Equipment', 'Equipment Blind Spots', 'Pinch Points'],
    riskLevel: 'Medium',
    controls: 'Be aware of surroundings, honk around corners, drive at safe speed, spotter when needed',
    preselected: true
  },
  {
    task: 'Housekeeping / Cleanup',
    hazards: ['Exposure to Dust', 'Slips / Trips Possible'],
    riskLevel: 'Low',
    controls: 'No dry sweeping, use dust pan, wear respirator/dust mask, keep walkways clear',
    preselected: true
  },
  {
    task: 'Loading / Unloading materials',
    hazards: ['Pinch Points', 'Operating Equipment', 'Working with Hand Tools'],
    riskLevel: 'Medium',
    controls: 'Proper lifting techniques, team lift for heavy items, gloves, steel toe boots'
  },
  {
    task: 'Setting up / Dismantling scaffold',
    hazards: ['Use of Scaffolds', 'Fall from Heights', 'Pinch Points'],
    riskLevel: 'Medium',
    controls: 'Inspect scaffold tags, fall protection above 10ft, hard hat, secure all components'
  },
  {
    task: 'Working on scaffold',
    hazards: ['Use of Scaffolds', 'Fall from Heights', 'Others Working Above / Below'],
    riskLevel: 'Medium',
    controls: 'Inspect scaffold daily, guardrails in place, no overloading, fall arrest if required'
  },
  {
    task: 'Working from ladder',
    hazards: ['Working from Ladder', 'Fall from Heights', 'Slips / Trips Possible'],
    riskLevel: 'Medium',
    controls: 'Inspect ladder before use, maintain 3-point contact, secure / tie off the ladder, level footing, do not overreach'
  }
];

// ── TOOLBOX HAZARD ROWS (trade-neutral fallback; an org/pack schema row's
// meta.toolbox_hazards, resolved via metaFor, wins when present) ────────────
const TOOLBOX_HAZARDS = [
  'Working at height / scaffold / ladder use',
  'Mobile equipment / vehicles in work area',
  'Silica / dust exposure — cutting, grinding, drilling',
  'Manual handling — heavy or awkward materials',
  'Adverse weather — heat, cold, rain, wind'
];

// ── DAILY TAILGATE — DEFAULT PRE-CHECKED ITEMS ───────────────────────────────
// Standard daily-operations defaults; foremen uncheck what does not apply
// today. Keys must match TAILGATE_ITEMS labels (packs adjust both together).
const DEFAULT_TAILGATE_CHECKED = {
  // Permits & PPE
  'Task-Appropriate Gloves':        true,
  'Eye Protection (safety glasses)': true,
  'Respirator / Dust Mask':         true,
  'Hard Hat & Safety Boots':        true,
  // Work Surfaces & Equipment
  'Uneven or Slippery Surfaces':    true,
  'Mobile Equipment in Area':       true,
  'Telehandler / Forklift Inspected': true,
  'Material Storage & Securement':  true,
  'Scaffold Inspected & Tagged':    true,
  // Site Conditions
  'Dust / Silica Exposure (cutting, grinding)': true,
  'Manual Material Handling':       true,
  'Sharp Edges & Protruding Objects': true,
  'Housekeeping Maintained':        true,
  // Fall Protection
  'Guardrails in Place':            true,
  'Full Body Harness Required':     true
};

// ── FLRA HAZARDS LIST ─────────────────────────────────────────────────────────
const FLRA_HAZARDS = [
  'Others Working Above / Below',
  'Working with Power Tools',
  'Working with Hand Tools',
  'Exposure to Heat / Cold',
  'Use of Scaffolds',
  'Operating Equipment',
  'Slips / Trips Possible',
  'Exposure to Dust',
  'Fall from Heights',
  'Working from Ladder',
  'Weather Conditions',
  'Pinch Points',
  'Working in Tight Spaces',
  'Equipment Blind Spots',
  'Noise Exposure',
  'Impalement',
  'Working at Heights',
  'Other'
];

// ── REGULATORY NOTICES (org-configurable — roadmap §4b) ──────────────────────
// The compliance line printed on inspection forms/PDFs is org CONFIGURATION
// (organizations.jurisdiction), not hardcoded content. 'generic' names no
// regulator and is the default for new tenants; unknown values fall back to
// generic. New jurisdictions (Alberta OHS, OSHA…) are added here as authored
// variants in Phase 2 — do not invent citations.
const REGULATORY_NOTICES = {
  generic: {
    mobile_equipment: 'In accordance with applicable occupational health and safety regulations — Mobile Equipment',
    scaffolds:        'In accordance with applicable occupational health and safety regulations — Scaffolds & Temporary Work Platforms'
  },
  bc: {
    mobile_equipment: 'In accordance with WorkSafeBC OHS Regulation Part 16 — Mobile Equipment',
    scaffolds:        'In accordance with WorkSafeBC OHS Regulation Part 13 — Scaffolds & Temporary Work Platforms'
  }
};

// Resolve the notice line for an inspection config, with provenance for the
// PDF (jurisdictions rollout 2026-07-26). Precedence:
//   1. Org override (organizations.reg_notice_overrides — the org's own
//      wording, their legal responsibility) → "Customized by {org}".
//   2. Seeded jurisdictions table content (rides the org context cache) →
//      "Seeded from {source} ({as_of}) — verify current requirements."
//   3. Hardcoded REGULATORY_NOTICES above (offline cold boot / old cache) —
//      no provenance line, the pre-rollout behavior.
// Returns { text, provenance } — provenance '' when there is none.
function resolveRegNoticeMeta(config) {
  if (!config) return { text: '', provenance: '' };
  if (!config.noticeKey) return { text: config.notice || '', provenance: '' };

  const org = window.currentOrg || {};
  const key = config.noticeKey;

  const override = org.regNoticeOverrides && String(org.regNoticeOverrides[key] || '').trim();
  if (override) return { text: override, provenance: `Customized by ${org.name || 'the organization'}` };

  const seeded = org.jurisdictionContent && org.jurisdictionContent.notices &&
                 String(org.jurisdictionContent.notices[key] || '').trim();
  if (seeded) {
    const p = org.jurisdictionContent.provenance || {};
    const provenance = p.source
      ? `Seeded from ${p.source}${p.as_of ? ` (${p.as_of})` : ''} — verify current requirements.`
      : '';
    return { text: seeded, provenance };
  }

  const jur = org.jurisdiction || 'generic';
  const set = REGULATORY_NOTICES[jur] || REGULATORY_NOTICES.generic;
  return { text: set[key] || REGULATORY_NOTICES.generic[key] || config.notice || '', provenance: '' };
}

// Text-only wrapper — the on-screen form header (app.js) needs just the line.
function resolveRegNotice(config) {
  return resolveRegNoticeMeta(config).text;
}

// ── Schema-driven widget content (Fase 2 TP1) ────────────────────────────────
// Composite widgets ask here for their content. Precedence:
//   1. An ORG-SCOPED form_schemas row (imported trade pack / future builder
//      edits) — its self-contained meta is authoritative.
//   2. Otherwise the in-code constants below (which applyTradePacks may have
//      overlaid) — identical to the system templates, and the offline-cold
//      fallback. System rows are NOT consulted: they mirror these constants,
//      and skipping them preserves the W5e flag-overlay behavior exactly.
// `key` supports dot paths into meta (e.g. 'flra.tasks').
function metaFor(type, key, fallback) {
  try {
    // Sheet editor's draft override: while the builder previews an unsaved
    // draft, its meta is the truth for THIS form type — the published row
    // would show yesterday's content. Set/cleared around generatePDF only.
    const draft = window._draftFormMeta;
    if (draft && draft.type === type) {
      const dv = String(key).split('.')
        .reduce((o, k) => (o == null ? o : o[k]), draft.meta || {});
      if (dv !== undefined && dv !== null) return dv;
      return fallback;
    }
    const row = window.Ordinex?.forms?.resolve(type,
      typeof activeTradePack === 'function' ? activeTradePack() : null);
    if (!row || row.org_id == null) return fallback;
    const v = String(key).split('.')
      .reduce((o, k) => (o == null ? o : o[k]), row.schema?.meta || {});
    return (v === undefined || v === null) ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

// ── Fase B: org schema.fields is authoritative ────────────────────────────────
// Same contract as metaFor: an ORG row (org_id != null) wins; the in-code
// FORM_FIELDS stay as the fallback for system rows / no row / offline-cold
// boot. This is the single seam that makes form STRUCTURE tenant-editable —
// renderer, validation, review summary and the generic PDF all call this.
//
// Compliance guard-rail: a tenant schema may never drop the fields that make
// a record a record — the signature is what locks it, crew-signin is the
// attendance evidence, supervisor_name is who answered for it. If an org row
// lost one the system template has (hand-edited SQL, importer bug), it is
// re-attached at the end in template order. The builder UI refuses the
// removal too; this is defense in depth.
const PROTECTED_FIELD_TYPES = ['signature', 'gc-signature', 'crew-signin'];
const PROTECTED_FIELD_IDS   = ['supervisor_name'];

function fieldsFor(type) {
  return _accountSigner(type, _legacyCopy(type, _fieldsForRaw(type)));
}
function _fieldsForRaw(type) {
  const base = FORM_FIELDS[type] || [];
  try {
    const row = window.Ordinex?.forms?.resolve(type,
      typeof activeTradePack === 'function' ? activeTradePack() : null);
    if (!row || row.org_id == null) return base;
    const custom = row.schema?.fields;
    if (!Array.isArray(custom) || custom.length === 0) return base;
    const have = new Set(custom.map(f => f && f.id));
    const missing = base.filter(f =>
      (PROTECTED_FIELD_TYPES.includes(f.type) || PROTECTED_FIELD_IDS.includes(f.id)) &&
      !have.has(f.id));
    return missing.length ? [...custom, ...missing] : custom;
  } catch (_) {
    return base;
  }
}

// Identity = the account (decided 2026-07-29). These forms asked the
// supervisor to type his own name every day — 17 spellings for 8 foremen in
// 60 days — and it is the name the PDF prints as who signed. Timesheet and EWO
// already prefill and lock it (autofill 'foreman'); the same flag goes on here
// at read time, so the built-in form AND an org's published copy are locked
// with no database edit (FORM_FIELDS mirrors the live system rows — see
// test/unit/demo-forms.test.mjs). Stored submissions keep the name they were
// filed with.
const ACCOUNT_SIGNED_FORMS = ['Daily Tailgate', 'Safety Inspection', 'Hazard Observation', 'Site Photos Only'];
function _accountSigner(type, fields) {
  if (!ACCOUNT_SIGNED_FORMS.includes(type)) return fields;
  return fields.map(f => (f && !f.autofill && (f.type || 'text') === 'text'
    && (f.id === 'supervisor_name' || /^\s*supervisor name\s*$/i.test(f.label || '')))
    ? { ...f, autofill: 'foreman' } : f);
}

// The "location" box of the timesheet / production report as first seeded (in
// FORM_FIELDS, the live system rows and org copies): a Trade Mark job as the
// example, and "Job" asked again under the job picker. Swapped at read time,
// only while it is still exactly the seeded text — anything an admin wrote
// stays as written. The id (and the PDF's "Location" line) do not change.
const LEGACY_LOCATION = {
  label: { 'Job / Location / Area': 'Area / Level (optional)' },
  placeholder: { 'e.g. Langley Smith Athletic Park — Level 3': 'e.g. Building B — Level 3' },
};
function _legacyCopy(type, fields) {
  if (type !== 'Weekly Timesheet' && type !== 'Production Report') return fields;
  return fields.map(f => {
    if (!f || f.id !== 'location') return f;
    const label = LEGACY_LOCATION.label[f.label] || f.label;
    const placeholder = LEGACY_LOCATION.placeholder[f.placeholder] || f.placeholder;
    return label === f.label && placeholder === f.placeholder ? f : { ...f, label, placeholder };
  });
}

// ── INSPECTION CONFIGS ────────────────────────────────────────────────────────
const INSP_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const INSPECTION_CONFIGS = {
  'Telehandler Inspection': {
    subtitle: 'Telehandler — Daily Inspection Checklist',
    noticeKey: 'mobile_equipment',
    footerNote: 'Form must be completed prior to start of shift. Keep on-site for review. Report any defects to the site supervisor immediately.',
    signOffLabel: 'Competent Person Sign-Off',
    sections: {
      'Engine & Fluids': [
        'Fluid levels — engine oil, hydraulic fluid, coolant',
        'Check for any visible leaks (oil, hydraulic, fuel)',
        'Fuel level sufficient for shift'
      ],
      'Tires & Undercarriage': [
        'Tires — pressure and condition (no cuts, bulges, or damage)',
        'Wheels and wheel nuts secure'
      ],
      'Boom & Forks / Attachments': [
        'Forks / attachments secure and not cracked or bent',
        'Boom function — lift, extend, retract smoothly',
        'Hydraulic lines — no leaks, cracks, or abrasion',
        'Carriage and tilt function operating correctly'
      ],
      'Controls & Safety Devices': [
        'Brakes and steering functioning correctly',
        'Lights, horn, and backup alarm operational',
        'Seatbelt in good condition and operational',
        'All safety decals visible and legible',
        'Fire extinguisher present, charged, and in date',
        'Emergency stop / shutdown function tested'
      ],
      'General Condition': [
        'Any visible structural damage or cracks',
        'Cab and controls — clean and free of obstructions',
        'No unauthorized modifications to machine'
      ]
    }
  },

  'E-Pallet Jack Inspection': {
    subtitle: 'Electric Pallet Jack — Daily Safety Checklist',
    notice: 'Operator must complete this checklist before starting the machine each day.',
    footerNote: 'Report any problems identified in the daily check to the site supervisor and/or office immediately.',
    signOffLabel: 'Inspected and Signed By',
    sections: {
      'Physical Checks': [
        'Inspect for any visible damage or wear',
        'Check for leaks (hydraulic fluid, etc.)',
        'Ensure forks are straight and not bent',
        'Check for any cracks or structural damage',
        'Inspect wheels — check for foreign objects stuck in wheels',
        'Verify battery charge level',
        'Check battery terminals for corrosion',
        'Inspect battery for signs of swelling or damage'
      ],
      'Controls & Safety Devices': [
        'Ensure all controls are functional (lift, lower, forward, reverse)',
        'Test the functionality of the brakes',
        'Test emergency stop button',
        'Check for working horn and lights',
        'Ensure all safety decals are visible and legible',
        'Verify operational labels are in place'
      ],
      'Operational Checks': [
        'Review starting and stopping procedures',
        'Discuss the importance of slow, controlled movements',
        'Discuss the maximum load capacity of the pallet jack',
        'Explain proper weight distribution on the forks',
        'Review procedures for operating on different surfaces',
        'Discuss safe practices in different weather conditions'
      ],
      'Safety & PPE': [
        'Verify operator is wearing required PPE',
        'Review emergency and malfunction procedures',
        'Discuss strategies for maneuvering in confined areas',
        'Discuss awareness of surroundings to avoid collisions',
        'Train crew on correct way to lift and lower loads',
        'Keep load low for stability during travel'
      ]
    }
  },

  'Scaffolding Inspection': {
    subtitle: 'Scaffolding — Daily Inspection Checklist',
    noticeKey: 'scaffolds',
    footerNote: 'Form must be completed prior to start of shift. Keep on-site for review. Report any defects to the site supervisor immediately.',
    signOffLabel: 'Competent Person Sign-Off',
    sections: {
      'Structure & Stability': [
        'Overall scaffold condition and stability',
        'Base supports (mud sills, base plates) properly placed and secure',
        'Uprights and frames plumb, undamaged, and secure',
        'Cross-bracing and connections intact and correctly installed'
      ],
      'Platforms & Access': [
        'Scaffold platforms / planks sound, secured, and of sufficient width',
        'Work platform fully decked — no gaps',
        'Access ladders / stairways secured, in good condition, and unobstructed'
      ],
      'Fall Protection': [
        'Guardrails, midrails, and toe boards installed and secure',
        'Fall protection in place where required (harnesses, tie-offs)',
        'No proximity hazards (electrical lines, overhead hazards)'
      ],
      'Load & Environmental Conditions': [
        'Scaffold not overloaded — material storage within safe load capacity',
        'Weather conditions checked (wind, ice, rain, slippery surfaces)'
      ],
      'Tagging System': [
        'Tag visible, accurate, and up to date for this scaffold'
      ]
    }
  },

  'Forklift Inspection': {
    // Names its own machine. It read "Telehandler / Forklift" — a leftover
    // from when the two sheets shared one config, so a forklift record
    // announced another machine in its header (2026-08-14).
    subtitle: 'Forklift — Daily Inspection Checklist',
    noticeKey: 'mobile_equipment',
    footerNote: 'Form must be completed prior to start of shift. Keep on-site for review. Report any defects to the site supervisor immediately.',
    signOffLabel: 'Competent Person Sign-Off',
    sections: {
      'Engine & Fluids': [
        'Fluid levels — engine oil, hydraulic fluid, coolant',
        'Check for any visible leaks (oil, hydraulic, fuel)',
        'Fuel level sufficient for shift'
      ],
      'Tires & Undercarriage': [
        'Tires — pressure and condition (no cuts, bulges, or damage)',
        'Wheels and wheel nuts secure'
      ],
      'Boom & Forks / Attachments': [
        'Forks / attachments secure and not cracked or bent',
        'Boom function — lift, extend, retract smoothly',
        'Hydraulic lines — no leaks, cracks, or abrasion',
        'Carriage and tilt function operating correctly'
      ],
      'Controls & Safety Devices': [
        'Brakes and steering functioning correctly',
        'Lights, horn, and backup alarm operational',
        'Seatbelt in good condition and operational',
        'All safety decals visible and legible',
        'Fire extinguisher present, charged, and in date',
        'Emergency stop / shutdown function tested'
      ],
      'General Condition': [
        'Any visible structural damage or cracks',
        'Cab and controls — clean and free of obstructions',
        'No unauthorized modifications to machine'
      ]
    }
  }
};

// ── QAQC INSPECTION ITEMS (trade-neutral fallback; an org/pack schema row's
// meta.qaqc_items, resolved via metaFor, wins when present) ─────────────────
const QAQC_ITEMS = [
  'Completed work matches drawings and approved scope',
  'Dimensions, lines and levels within tolerance',
  'Materials as per approved submittals / specifications',
  'Fastening, anchors and connections correctly installed',
  'Openings (doors / frames) plumb, square, correctly sized',
  'Penetrations sealed / firestopping completed where required',
  'Adjacent surfaces protected — no damage, stains or residue',
  'Deficiencies from previous inspection corrected',
  'Area clean — debris and off-cuts removed'
];

// ── FIELD QAQC (ITP) — staged inspection items ────────────────────────────────
// Aligned with GC ITP expectations: inspection/hold points per stage of the
// work, methods embedded in the item text, acceptance = drawings/spec (04050
// Masonry Procedures, 04200 Unit Masonry, CSA A371/S304). Kept deliberately
// simple for field use — one PASS/FAIL/N/A + comment per item.
const FIELD_QAQC_STAGES = [
  {
    key: 'checklist_pre', stage: 'Stage 1 — Pre-Installation',
    items: [
      'Approved materials on site — block type/size and mortar match submittals',
      'Materials stored off ground, covered and protected from weather',
      'Layout, dimensions and openings verified against drawings',
      'Dowels / starter bars — correct size, spacing and location',
      'Hot / cold weather procedures in place (Spec 04050), if applicable',
    ],
  },
  {
    key: 'checklist_during', stage: 'Stage 2 — Installation',
    items: [
      'Bond pattern and coursing as per drawings',
      'Joints consistent thickness and fully tooled',
      'Wall plumb, level and true to line within tolerance',
      'Horizontal joint reinforcement — size and spacing per spec',
      'Vertical reinforcement placed and grouted per drawings',
      'Bond beams / lintels installed per drawings',
      'Control joints located and formed per drawings',
      'Ties / anchors (Wall-Lok) installed per spec',
    ],
  },
  {
    key: 'checklist_post', stage: 'Stage 3 — Completion',
    items: [
      'Openings plumb, square and correct size',
      'Walls cleaned — no mortar smears, stains or efflorescence',
      'Firestopping complete and penetrations sealed',
      'Work area clean — debris and mortar droppings removed',
      'Progress photos taken and attached to this report',
    ],
  },
];

// CLOSEOUT_QUESTIONS lived here — four end-of-day questions with a widget, a
// PDF block and a form-builder card behind them, and no form that ever used
// them: zero schemas with a `closeout` field, zero records with closeout data.
// The whole chain was removed 2026-08-14 rather than left looking like a
// feature. It is in the git history if the close-out is ever wanted for real.

// ── FORM FIELDS ───────────────────────────────────────────────────────────────
const FORM_FIELDS = {
  'Daily Tailgate': [
    { id: 'work_scope',        label: 'Daily Work Scope',                   type: 'textarea', placeholder: 'Describe today\'s work scope...', required: true },
    { id: 'project_concerns',  label: 'Project Concerns and Controls',      type: 'textarea', placeholder: 'e.g. Road closed, critical lift...' },
    { id: 'items_reviewed',    label: 'Items Reviewed',                     type: 'tailgate-items' },
    { id: 'flra',              label: 'Field Level Risk Assessment',        type: 'flra-table' },
    { id: 'crew',              label: 'Crew Sign-In',                       type: 'crew-signin' },
    { id: 'supervisor_name',   label: 'Supervisor Name',                    type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',         label: 'Supervisor Sign Off',                type: 'signature' }
  ],

  'Safety Inspection': [
    { id: 'inspection_area',   label: 'Inspection Area / Location',         type: 'text', placeholder: 'e.g. Level 3 North Side', required: true },
    // Item by item, not a tick list. As a checkbox-group this recorded only
    // what WAS inspected and the PDF printed the ticked labels as one run-on
    // line — so an item nobody looked at simply vanished from the record,
    // which on a safety inspection is the finding (2026-08-14). Same widget
    // the equipment sheets use: every item prints with its state, and a
    // deficiency can carry the note that explains it.
    { id: 'items_inspected',   label: 'Items Inspected',                    type: 'checklist',
      items: ['PPE Availability & Use','Fall Protection Installed','Housekeeping','Equipment Condition','Electrical Safety','Fire Extinguishers','First Aid Kit','Emergency Exits Clear','Signage in Place','Scaffolding / Shoring','Tool Condition','Chemical Storage'] },
    { id: 'deficiencies',      label: 'Deficiencies Found',                 type: 'textarea', placeholder: 'List any issues or non-conformances...' },
    { id: 'corrective_actions',label: 'Corrective Actions',                 type: 'textarea', placeholder: 'Actions taken or planned...' },
    { id: 're_inspection_date',label: 'Re-Inspection Date (if required)',   type: 'date' },
    { id: 'supervisor_name',   label: 'Supervisor Name',                    type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',         label: 'Supervisor Sign Off',                type: 'signature' }
  ],

  'Weekly Toolbox Talk': [
    // ① WHAT ARE WE DOING THIS WEEK?
    { id: '_section_scope',       label: '① What Are We Doing This Week?',           type: 'section' },
    { id: 'work_scope',           label: 'Brief description of scope',               type: 'textarea', placeholder: 'Helps crew understand context for hazards below...', required: true },

    // ② SAFETY TOPIC OF THE WEEK
    { id: '_section_topic',       label: '② Safety Topic of the Week',              type: 'section' },
    { id: 'safety_topics',        label: 'Circle or check your topic(s)',            type: 'checkbox-group',
      options: [
        'Silica dust / respiratory protection',
        'Fall protection & scaffold inspection',
        'Telehandler / forklift safety',
        'PPE requirements',
        'Manual lifting / ergonomics',
        'Heat / cold stress',
        'Housekeeping & site organization',
        'Near miss / incident review',
        'Lock out / tag out',
        'Emergency procedures & muster point'
      ]
    },
    { id: 'topic_other',          label: 'Other topic (if selected above)',          type: 'text',     placeholder: 'Describe other topic...' },
    { id: 'key_points',           label: 'Key Points Discussed',                    type: 'textarea', placeholder: 'Summarize the main safety points covered...', required: true },

    // ③ HAZARD REVIEW
    { id: '_section_hazards',     label: '③ Hazard Review',                         type: 'section' },
    { id: 'hazard_review',        label: 'Identify what could go wrong and how to control it', type: 'toolbox-hazards' },

    // ④ FOLLOW-UP FROM LAST MEETING
    { id: '_section_followup',    label: '④ Follow-Up from Last Meeting',           type: 'section' },
    { id: 'incidents_since_last', label: 'Any incidents or near misses since last meeting?', type: 'radio', options: ['Yes', 'No'], required: true },
    { id: 'open_action_items',    label: 'Any open action items from last week?',   type: 'radio', options: ['Yes', 'No'], required: true },

    // ⑤ CREW ATTENDANCE
    { id: '_section_crew',        label: '⑤ Crew Attendance',                       type: 'section' },
    { id: 'crew',                 label: 'Print name to confirm attendance',        type: 'crew-signin' },

    // FOREMAN SIGN-OFF
    { id: '_section_signoff',     label: 'Foreman Sign-Off',                        type: 'section' },
    { id: 'supervisor_name',      label: 'Foreman — Printed Name',                 type: 'text',     required: true, autofill: 'foreman' },
    { id: 'signature',            label: 'Foreman — Signature',                    type: 'signature' }
  ],

  'Incident Report': [
    // ── INCIDENT INFORMATION ──────────────────────────────────────────────
    { id: '_section_incident',   label: '📋 Incident Information',          type: 'section' },
    { id: 'incident_location',   label: 'Incident Location',                type: 'text',     placeholder: 'Exact location on site', required: true },
    { id: 'incident_datetime',   label: 'Date & Time of Incident',          type: 'text',     placeholder: 'e.g. 2025-05-08  14:30', required: true },

    // ── PERSON GIVING STATEMENT ───────────────────────────────────────────
    { id: '_section_person',     label: '👤 Person Giving Statement',       type: 'section' },
    { id: 'stmt_full_name',      label: 'Full Name',                        type: 'text',     placeholder: 'Full legal name', required: true },
    { id: 'stmt_phone',          label: 'Phone',                            type: 'text',     placeholder: 'Phone number' },
    { id: 'stmt_address',        label: 'Address',                          type: 'text',     placeholder: 'Home address' },
    { id: 'stmt_trade',          label: 'Trade / Occupation',               type: 'text',     placeholder: 'e.g. Labourer, Operator' },
    { id: 'stmt_years_exp',      label: 'Years of Experience',              type: 'text',     placeholder: 'e.g. 5 years' },
    { id: 'stmt_other_exp',      label: 'Other Experience',                 type: 'text',     placeholder: 'Other relevant experience' },
    { id: 'stmt_employer',       label: 'Employer',                         type: 'text',     placeholder: 'Company name' },
    { id: 'stmt_was_present',    label: 'Were you present when the incident occurred?', type: 'radio',
      options: ['Yes', 'No'], required: true },
    { id: 'stmt_did_see',        label: 'Did you see it happen?',           type: 'radio',
      options: ['Yes', 'No'], required: true },

    // ── INCIDENT STATEMENT ────────────────────────────────────────────────
    { id: '_section_statement',  label: '📝 Incident Statement',            type: 'section' },
    { id: 'statement',           label: 'In your own words, state what you saw or what you know about this incident. Include dates/times and any specifics or details you feel are relevant. Answer as applicable: Who? What? Where? When? How? Why?',
                                                                            type: 'textarea', placeholder: 'Write your full statement here...', required: true },
    { id: 'continuation',        label: 'Continuation of Statement (if needed)', type: 'textarea', placeholder: 'Continue statement here...' },

    // ── STATEMENT TAKEN BY ────────────────────────────────────────────────
    { id: '_section_takenby',    label: '🖊️ Statement Taken By',           type: 'section' },
    { id: 'taken_by_name',       label: 'Name',                            type: 'text',     placeholder: 'Name of person taking statement', required: true },
    { id: 'taken_by_position',   label: 'Position',                        type: 'text',     placeholder: 'e.g. Foreman, Supervisor' },
    { id: 'taken_by_company',    label: 'Company',                         type: 'text',     placeholder: 'Company name' },

    // ── WITNESS DECLARATION ───────────────────────────────────────────────
    { id: '_section_witness',    label: '✍️ Witness Declaration & Signature', type: 'section' },
    { id: 'signature',           label: 'Witness Signature',               type: 'signature' }
  ],

  'Hazard Observation': [
    { id: 'hazard_location',   label: 'Hazard Location',                    type: 'text', placeholder: 'Where on site?', required: true },
    { id: 'hazard_description',label: 'Hazard Description',                 type: 'textarea', placeholder: 'Describe the unsafe condition...', required: true },
    { id: 'risk_level',        label: 'Risk Level',                         type: 'radio',
      options: ['Low — Monitor only','Medium — Address within 24hrs','High — Fix before work continues','Critical — Stop work immediately'], required: true },
    { id: 'immediate_action',  label: 'Immediate Action Taken',             type: 'textarea', placeholder: 'What was done right away?' },
    { id: 'follow_up_required',label: 'Follow-Up Required?',                type: 'radio', options: ['Yes','No'], required: true },
    { id: 'follow_up_details', label: 'Follow-Up Details',                  type: 'textarea', placeholder: 'Who, what, and by when?' },
    { id: 'supervisor_name',   label: 'Supervisor Name',                    type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',         label: 'Sign Off',                           type: 'signature' }
  ],

  'Site Photos Only': [
    { id: 'description',       label: 'Description / Purpose of Photos',    type: 'textarea', placeholder: 'What are these photos documenting?', required: true },
    { id: 'area',              label: 'Area / Section of Site',             type: 'text', placeholder: 'Which area is shown?' },
    { id: 'supervisor_name',   label: 'Submitted By',                       type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',         label: 'Sign Off',                           type: 'signature' }
  ],

  'Production Report': [
    // Neutral labels: this form serves every trade now, and the crew columns
    // came out in 2026-06-01 (labour is costed from timesheets / sub rates).
    // The old placeholder named a real Trade Mark jobsite — not something to
    // show a roofing tenant in Ontario.
    // Same gap the timesheet had: the PDF header prints "Week Of" and a GC
    // line, and neither had a field behind it (2026-08-14). A production
    // record goes to the general contractor — it should name them.
    { id: 'week_of',         label: 'Week Of',                type: 'date', required: true },
    { id: 'gc',              label: 'General Contractor',     type: 'text', placeholder: 'e.g. Northpoint Construction' },
    { id: 'location',        label: 'Job / Location / Area',  type: 'text', placeholder: 'e.g. Building B — Level 3' },
    { id: 'production',      label: 'Units Placed',           type: 'production-table' },
    { id: 'delays',          label: 'Delays / Issues',        type: 'textarea', placeholder: 'Brief note on anything that impacted production...' },
    { id: 'supervisor_name', label: 'Foreman Name',           type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',       label: 'Foreman Sign Off',       type: 'signature' }
  ],

  'Telehandler Inspection': [
    { id: 'machine_unit',        label: 'Machine / Unit #',       type: 'text', placeholder: 'e.g. TH-01', required: true },
    // The PDF letterhead has always printed an Operator line for every machine
    // inspection (generateDailyInspectionPDF), but only the pallet jack form
    // offered the field — telehandler and forklift printed "Operator —" on
    // every record, with no way to fill it (2026-08-13). Same shape as
    // E-Pallet Jack Inspection below, which had it right from the start.
    { id: 'operator',            label: 'Operator Name',           type: 'text', placeholder: 'Operator name', required: true },
    { id: 'checklist',           label: 'Daily Inspection',        type: 'daily-inspection' },
    { id: 'final_determination', label: 'Final Determination',     type: 'radio',
      options: ['Machine is SAFE for use as per today\'s inspection', 'Machine REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'add_comments',        label: 'Additional Comments',     type: 'textarea', placeholder: 'Any additional notes or deficiencies found...' },
    { id: 'supervisor_name',     label: 'Competent Person Name',   type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',           label: 'Competent Person Sign Off', type: 'signature' }
  ],

  'Forklift Inspection': [
    { id: 'machine_unit',        label: 'Machine / Unit #',       type: 'text', placeholder: 'e.g. FK-01', required: true },
    // See the note on Telehandler Inspection above — same missing field.
    { id: 'operator',            label: 'Operator Name',           type: 'text', placeholder: 'Operator name', required: true },
    { id: 'checklist',           label: 'Daily Inspection',        type: 'daily-inspection' },
    { id: 'final_determination', label: 'Final Determination',     type: 'radio',
      options: ['Machine is SAFE for use as per today\'s inspection', 'Machine REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'add_comments',        label: 'Additional Comments',     type: 'textarea', placeholder: 'Any additional notes or deficiencies found...' },
    { id: 'supervisor_name',     label: 'Competent Person Name',   type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',           label: 'Competent Person Sign Off', type: 'signature' }
  ],

  'E-Pallet Jack Inspection': [
    { id: 'machine_unit',    label: 'Machine / Unit #',       type: 'text', placeholder: 'e.g. EPJ-01', required: true },
    { id: 'operator',        label: 'Operator Name',           type: 'text', placeholder: 'Operator name', required: true },
    { id: 'checklist',       label: 'Daily Safety Checklist',  type: 'daily-inspection' },
    // The conclusion a pre-use inspection exists to record. Telehandler,
    // forklift and scaffolding all had one; the pallet jack was the only
    // machine whose sheet ended without saying whether it may be used
    // (2026-08-14). Same wording as the other two machine sheets.
    { id: 'final_determination', label: 'Final Determination', type: 'radio',
      options: ['Machine is SAFE for use as per today\'s inspection', 'Machine REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'add_comments',    label: 'Comments',                type: 'textarea', placeholder: 'Report any problems identified to the site supervisor immediately...' },
    { id: 'supervisor_name', label: 'Printed Name',            type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',       label: 'Operator Sign Off',       type: 'signature' }
  ],

  'Scaffolding Inspection': [
    { id: 'scaffold_location',   label: 'Scaffold Location',       type: 'text', placeholder: 'e.g. Level 3 North Wall', required: true },
    { id: 'inspector',           label: 'Inspector Name',           type: 'text', placeholder: 'Inspector name', required: true },
    { id: 'checklist',           label: 'Daily Inspection Items',   type: 'daily-inspection' },
    { id: 'tag_status',          label: 'Scaffold Tag Status',      type: 'radio',
      options: ['🟢 GREEN — Scaffold is SAFE for use', '🟡 YELLOW — CAUTION: limited use, mitigations in place', '🔴 RED — DO NOT USE: scaffold is unsafe'],
      required: true },
    { id: 'final_determination', label: 'Final Determination',      type: 'radio',
      options: ['Scaffold is SAFE for use as per today\'s inspection', 'Scaffold REQUIRES REPAIR before use — do not use'],
      required: true },
    { id: 'add_comments',        label: 'Additional Comments',      type: 'textarea', placeholder: 'Any additional notes or corrective actions...' },
    { id: 'supervisor_name',     label: 'Competent Person Name',    type: 'text', placeholder: 'Print full name', required: true },
    { id: 'signature',           label: 'Competent Person Sign Off', type: 'signature' }
  ],

  'QAQC - Foreman': [
    { id: 'project_number',    label: 'Project #',                          type: 'text',     autofill: 'project_code' },
    { id: 'gc',                label: 'General Contractor',                 type: 'text',     placeholder: 'GC company on site (e.g. Ledcor)' },
    { id: 'location_area',     label: 'Location / Area',                    type: 'text',     placeholder: 'e.g. Level 3 — North Wall', required: true },
    { id: 'floor_room',        label: 'Floor / Room',                       type: 'text',     placeholder: 'e.g. Floor 4, Room 402' },
    { id: 'drawing_ref',       label: 'Drawing Ref #',                      type: 'text',     placeholder: 'e.g. A-201 Rev 3' },
    { id: 'work_description',  label: 'Work Description',                   type: 'textarea', placeholder: 'Brief description of the completed scope...' },
    { id: 'checklist',         label: 'Inspection Items',                   type: 'qaqc-table' },
    { id: 'overall_result',    label: 'Overall Result',                     type: 'radio',
      options: ['ALL ITEMS PASS — AREA ACCEPTED', 'HOLD — ITEMS REQUIRE CORRECTION BEFORE ACCEPTANCE'], required: true },
    { id: 'corrective_notes',  label: 'Deficiencies / Corrective Actions',  type: 'textarea', placeholder: 'If anything failed: what needs fixing, by whom, by when...' },
    { id: 'reinspection_date', label: 'Re-Inspection Date (if on hold)',    type: 'date' },
    { id: 'supervisor_name',   label: 'Contractor Representative (PM / Foreman)', type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Contractor Sign Off',                type: 'signature' },
    { id: 'gc_name',           label: 'GC Representative Name',             type: 'text',     placeholder: 'Who walked the area with you' },
    // The GC signs the acceptance right here, on the phone — no more "signs
    // on paper". Same widget the Extra Work Order uses for its GC sign-off.
    { id: 'gc_signature',      label: 'GC Representative Sign Off',         type: 'gc-signature' }
  ],

  'Weekly Timesheet': [
    // The PDF has always printed a "Week Ending" line in its header and there
    // was no field behind it, so every timesheet ever filed showed an em dash
    // where the pay period goes — and dated itself from the day it was filled
    // (2026-08-14). A timesheet submitted Monday for last week is normal, so
    // the week is stated, not derived.
    { id: 'week_ending',     label: 'Week Ending',          type: 'date', required: true },
    { id: 'location',        label: 'Job / Location / Area', type: 'text', placeholder: 'e.g. Langley Smith Athletic Park — Level 3' },
    { id: 'timesheet',       label: 'Employee Hours',        type: 'timesheet-table' },
    { id: 'supervisor_name', label: 'Foreman Name',          type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',       label: 'Foreman Sign Off',      type: 'signature' }
  ],

  // Extra Work Order — foreman records WHO and WHAT (hours + materials).
  // No prices, no costs, no $ on the foreman side. Admin fills rates and
  // finalises pricing from the "Extra Work" admin tab. Foreman also captures
  // GC supervisor name + signature on-site at submit time.
  'Extra Work Order': [
    { id: 'ewo_number_display', label: 'EWO #',                 type: 'text',     autofill: 'ewo_number', readOnly: true },
    { id: 'work_description', label: 'Work Description',         type: 'textarea', required: true,
                              placeholder: 'Describe the extra work performed and why it is outside the original scope…' },
    { id: 'ewo_labour',      label: 'Labour — Who & How Many Hours', type: 'ewo-labour-table' },
    { id: 'ewo_materials',   label: 'Materials & Equipment Used',    type: 'ewo-materials-table' },
    { id: 'supervisor_name', label: 'Foreman Name',              type: 'text',     required: true, autofill: 'foreman' },
    { id: 'signature',       label: 'Foreman Sign Off',          type: 'signature' },
    { id: 'gc_name',         label: 'GC Supervisor Name',        type: 'text',
                             placeholder: 'Printed name of GC site supervisor who witnessed the work' },
    { id: 'gc_signature',    label: 'GC Supervisor Sign Off',    type: 'gc-signature' }
  ],

  // ── Drilling & Shotcrete pack forms ─────────────────────────────────────────
  // Specified field-by-field in docs/trade-packs/drilling-shotcrete/
  // forms-catalog.md. Equipment logs mirror the Telehandler/Forklift shape
  // (unit + checklist + final determination + sign-off) but use the editable
  // `checklist` piece so tenants can adjust items in the Form Builder.
  // Tenant fills its own Equipment ID — no fleet codes ship with the pack.

  'Drill Rig Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Engine oil level', 'Hydraulic oil level', 'Coolant level', 'Fuel level',
              'Air filter / pre-cleaner', 'Leaks — oil, fuel, hydraulic', 'Hoses & couplings condition',
              'Whip checks installed on air connections', 'Tracks / undercarriage', 'Boom, mast & feed condition',
              'Drifter / rotation head', 'Drill steel, rods & couplings', 'Centralizer / rod support',
              'Dust collection / suppression system', 'Controls & gauges functional', 'Emergency stop functional',
              'Guards & covers in place', 'Fire extinguisher on board', 'Backup alarm / horn', 'Lights',
              'Operator cab / ROPS condition', 'Housekeeping — deck clear of tools & debris'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  'Air Compressor Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Engine oil level', 'Compressor oil level', 'Coolant level', 'Fuel level',
              'Air filters condition', 'Belts & hoses', 'Leaks — oil, fuel, air',
              'Moisture drained from tank / receiver', 'Safety / relief valve intact', 'Pressure gauges functional',
              'Air hoses & couplings condition', 'Whip checks / safety pins on all connections',
              'Towing hitch, chains & lights (if towable)', 'Tires / wheels (if towable)', 'Guards in place',
              'Fire extinguisher', 'Emergency stop functional', 'Housekeeping around unit'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  'Concrete Pump Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Engine oil level', 'Hydraulic oil level', 'Coolant level', 'Fuel level',
              'Hopper grate in place & interlocked', 'Agitator condition', 'Wear plate & cutting ring',
              'Delivery line condition (pipes & elbows)', 'Clamps & gaskets — seated & pinned',
              'Delivery hoses condition (no soft spots)', 'Whip check / safety sling at discharge hose',
              'Water pump & wash-out system', 'Pressure gauges functional', 'Outriggers / stability pads',
              'Controls & remote functional', 'Emergency stops functional', 'Guards in place',
              'Fire extinguisher', 'Leaks — oil, fuel, hydraulic', 'Housekeeping — slick line area clear'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  'Dry-Mix Pump Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Feed bowl / rotor & wear pads', 'Clamping pressure correct', 'Seals & gaskets condition',
              'Material hose condition (no soft spots or kinks)', 'Hose couplings pinned & whip-checked',
              'Air supply hose & connections', 'Water ring / nozzle water system', 'Water pressure adequate at nozzle',
              'Air filters / lubricator', 'Controls & gauges functional', 'Emergency stop functional',
              'Guards in place', 'Dust control at feed hopper', 'Fire extinguisher',
              'Leaks — air, water, oil', 'Housekeeping — rebound cleared'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  'Grout Plant Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Engine / motor condition', 'Hydraulic oil level (if applicable)', 'Mixer tank & paddles condition',
              'Screens in place & clean', 'Grout pump condition', 'Hoses & couplings — pinned & whip-checked',
              'Water meter functional', 'Pressure gauge functional', 'Relief / bypass valve intact',
              'Agitator tank condition', 'Controls functional', 'Emergency stop functional', 'Guards in place',
              'Leaks — water, grout, oil', 'Wash-out water contained', 'Fire extinguisher',
              'Housekeeping around plant'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  'Truck Monthly Inspection': [
    { id: 'equipment_id',      label: 'Vehicle / Unit ID',          type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model / Year',        type: 'text',   width: 'half' },
    { id: 'licence_plate',     label: 'Licence Plate',              type: 'text',   required: true, width: 'half' },
    { id: 'odometer_km',       label: 'Odometer (km)',              type: 'number', required: true, width: 'half' },
    { id: 'month_covered',     label: 'Month Covered',              type: 'text',   placeholder: 'e.g. 2026-07', required: true, width: 'half' },
    { id: 'checklist',         label: 'Monthly Inspection',         type: 'checklist', required: true,
      items: ['Headlights / signals / brake lights', 'Horn', 'Windshield & wipers', 'Mirrors',
              'Tires — tread & pressure', 'Wheels & lug nuts', 'Service brakes', 'Park brake',
              'Steering response', 'Fluid levels (oil, coolant, washer)', 'Leaks under vehicle', 'Seat belts',
              'Registration & insurance in vehicle', 'First aid kit stocked', 'Fire extinguisher charged',
              'Emergency triangles / flares', 'Securement points & straps', 'Hitch & towing equipment',
              'Body damage noted', 'Interior housekeeping'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Vehicle is SAFE for use as per this inspection', 'Vehicle REQUIRES REPAIR — remove from service'], required: true },
    { id: 'supervisor_name',     label: 'Inspected By',               type: 'text', required: true },
    { id: 'signature',           label: 'Sign Off',                   type: 'signature' }
  ],

  'Equipment Maintenance Record': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   required: true, width: 'half' },
    { id: 'equipment_type',    label: 'Equipment Type',             type: 'select', required: true, width: 'half',
      options: ['Excavator / loader', 'Lift / MEWP', 'Telehandler / forklift', 'Truck / vehicle',
                'Compressor / generator', 'Pump', 'Drill rig', 'Grout plant', 'Other'] },
    { id: 'hour_meter',        label: 'Hour Meter / Odometer',      type: 'number', width: 'half' },
    { id: 'work_type',         label: 'Work Type',                  type: 'radio',
      options: ['Scheduled service', 'Repair', 'Inspection follow-up'], required: true },
    { id: 'reported_problem',  label: 'Reported Problem / Reason',  type: 'textarea', required: true,
      placeholder: 'What triggered this work — daily log deficiency, breakdown, service interval...' },
    { id: 'work_performed',    label: 'Work Performed',             type: 'textarea', required: true },
    { id: 'parts_used',        label: 'Parts Used',                 type: 'table', maxRows: 10,
      columns: [ { key: 'part', label: 'Part / description', type: 'text' }, { key: 'qty', label: 'Qty', type: 'number' } ] },
    { id: 'performed_by',      label: 'Performed By',               type: 'text', required: true, width: 'half' },
    { id: 'vendor_wo',         label: 'Vendor / Work Order #',      type: 'text', width: 'half' },
    { id: 'next_service_due',  label: 'Next Service Due (hours or date)', type: 'text', width: 'half' },
    { id: 'back_in_service',   label: 'Back in Service?',           type: 'radio',
      options: ['Yes — returned to service', 'No — still down'], required: true },
    { id: 'photos',            label: 'Photos',                     type: 'photo' },
    { id: 'supervisor_name',   label: 'Recorded By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Incident Investigation': [
    { id: '_section_details',  label: '📋 Incident Details',        type: 'section' },
    { id: 'incident_datetime', label: 'Date & Time of Incident',    type: 'text',   placeholder: 'e.g. 2026-07-26  14:30', required: true, width: 'half' },
    { id: 'incident_location', label: 'Exact Location',             type: 'text',   required: true, width: 'half' },
    { id: 'classification',    label: 'Classification',             type: 'radio',  required: true,
      options: ['Near Miss', 'First Aid', 'Medical Aid', 'Time Loss', 'Property / Equipment Damage', 'Environmental'] },
    { id: 'reported_at',       label: 'Reported to Supervisor At',  type: 'text',   placeholder: 'Time reported' },
    { id: '_section_people',   label: '👤 People Involved',         type: 'section' },
    { id: 'involved_worker',   label: 'Injured / Involved Worker',  type: 'text',   placeholder: 'Blank for property-only events', width: 'half' },
    { id: 'occupation',        label: 'Occupation',                 type: 'text',   width: 'half' },
    { id: 'years_experience',  label: 'Years of Experience',        type: 'text',   width: 'half' },
    { id: 'witnesses',         label: 'Witnesses',                  type: 'textarea', placeholder: 'Names — take a statement per witness on the Incident Report form' },
    { id: '_section_what',     label: '📝 What Happened',           type: 'section' },
    { id: 'description',       label: 'Description of Event',       type: 'textarea', required: true, placeholder: 'Who, what, where, when, how...' },
    { id: 'injury_nature',     label: 'Body Part / Injury Nature',  type: 'text',   width: 'half' },
    { id: 'equipment_involved',label: 'Equipment / Materials Involved', type: 'text', width: 'half' },
    { id: 'photos',            label: 'Photos',                     type: 'photo' },
    { id: '_section_why',      label: '🔍 Why It Happened',         type: 'section' },
    { id: 'immediate_causes',  label: 'Immediate Causes',           type: 'textarea', required: true, placeholder: 'Unsafe acts and conditions...' },
    { id: 'root_causes',       label: 'Underlying / Root Causes',   type: 'textarea', required: true, placeholder: 'Keep asking "why" until process-level causes appear...' },
    { id: '_section_actions',  label: '✅ Actions',                 type: 'section' },
    { id: 'corrective_actions',label: 'Corrective Actions',         type: 'table', required: true, maxRows: 10,
      columns: [ { key: 'action', label: 'Action', type: 'text' }, { key: 'owner', label: 'Assigned to', type: 'text' },
                 { key: 'due', label: 'Due date', type: 'text' }, { key: 'done', label: 'Done', type: 'text' } ] },
    { id: 'area_released_at',  label: 'Work Area Released At',      type: 'text',   width: 'half' },
    { id: 'regulator_notified',label: 'Regulator Notified?',        type: 'radio',  required: true,
      options: ['Not required', 'Yes — reported'] },
    { id: 'rep_notified',      label: 'Union / Worker Rep Notified?', type: 'radio', options: ['Yes', 'No', 'N/A'] },
    { id: '_section_signoff',  label: '🖊️ Sign Off',               type: 'section' },
    { id: 'supervisor_name',   label: 'Investigated By',            type: 'text',   required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Investigator Sign Off',      type: 'signature' },
    { id: 'management_review', label: 'Management Review Name',     type: 'text' }
  ],

  'First Aid Assessment': [
    { id: '_section_worker',   label: '👤 Worker',                  type: 'section' },
    { id: 'worker_name',       label: 'Worker Name',                type: 'text', required: true, width: 'half' },
    { id: 'occupation',        label: 'Occupation',                 type: 'text', width: 'half' },
    { id: 'injury_datetime',   label: 'Date & Time of Injury / Illness', type: 'text', required: true, width: 'half' },
    { id: 'reported_datetime', label: 'Date & Time Reported',       type: 'text', width: 'half' },
    { id: '_section_assess',   label: '🩺 Assessment',              type: 'section' },
    { id: 'injury_nature',     label: 'Nature of Injury / Illness', type: 'textarea', required: true },
    { id: 'body_part',         label: 'Body Part Affected',         type: 'text', width: 'half' },
    { id: 'mechanism',         label: 'Mechanism (what caused it)', type: 'textarea' },
    { id: 'signs_symptoms',    label: 'Signs & Symptoms Observed',  type: 'textarea' },
    { id: '_section_treat',    label: '🩹 Treatment',               type: 'section' },
    { id: 'first_aid_given',   label: 'First Aid Provided',         type: 'textarea', required: true },
    { id: 'attendant_name',    label: 'Attendant Name',             type: 'text', required: true, width: 'half' },
    { id: 'attendant_cert',    label: 'Attendant Certification',    type: 'select', width: 'half',
      options: ['First aid certificate — basic', 'First aid certificate — intermediate', 'First aid certificate — advanced', 'Other'] },
    { id: '_section_outcome',  label: '📋 Outcome',                 type: 'section' },
    { id: 'disposition',       label: 'Disposition',                type: 'radio', required: true,
      options: ['Returned to regular duties', 'Modified duties', 'Referred to medical aid', 'Transported to hospital'] },
    { id: 'follow_up',         label: 'Follow-Up Required',         type: 'textarea' },
    { id: 'claim_started',     label: 'Claim / Report Started?',    type: 'radio', options: ['Yes', 'No'] },
    // signerField/signerRole: this record is signed by the ATTENDANT who
    // treated the worker, not by the supervisor. Without them the PDF titled
    // the block "Supervisor Sign Off" and signed it with the foreman's name,
    // attributing the treatment to whoever filed the form (2026-08-14).
    { id: 'signature',         label: 'Attendant Sign Off',         type: 'signature',
      signerField: 'attendant_name', signerRole: 'First Aid Attendant' }
  ],

  'Disciplinary Report': [
    { id: 'employee_name',     label: 'Employee Name',              type: 'text', required: true, width: 'half' },
    { id: 'occupation',        label: 'Occupation',                 type: 'text', width: 'half' },
    { id: 'occurrence_date',   label: 'Date of Occurrence',         type: 'date', required: true, width: 'half' },
    { id: 'category',          label: 'Category',                   type: 'checkbox-group', required: true,
      options: ['PPE Violation', 'Unsafe Practice', 'Unsafe Procedure', 'Unsafe Use of Tool(s)',
                'Safe Work Procedures Violation', 'Insubordination', 'Time Theft', 'Other'] },
    { id: 'description',       label: 'Description of Occurrence',  type: 'textarea', required: true, placeholder: 'Facts, not conclusions...' },
    { id: 'prior_warnings',    label: 'Prior Warnings on Record?',  type: 'radio', required: true,
      options: ['None', 'Verbal', 'Written', 'Multiple'] },
    { id: 'action_taken',      label: 'Action Taken',               type: 'radio', required: true,
      options: ['Verbal warning', 'Written warning', 'Suspension', 'Termination', 'Retraining'] },
    { id: 'action_details',    label: 'Details of Action / Retraining', type: 'textarea' },
    { id: 'rep_notified',      label: 'Union / Worker Representative Notified?', type: 'radio', required: true,
      options: ['Yes', 'No', 'N/A'] },
    { id: 'employee_comments', label: 'Employee Comments',          type: 'textarea' },
    { id: 'supervisor_name',   label: 'Supervisor Name',            type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Supervisor Sign Off',        type: 'signature' },
    { id: 'employee_ack',      label: 'Employee Acknowledgement (name)', type: 'text', placeholder: 'Signing ≠ agreeing; refusal noted in comments' }
  ],

  'Site Orientation Record': [
    { id: 'worker_name',       label: 'Worker Name',                type: 'text', required: true, width: 'half' },
    { id: 'employer',          label: 'Employer (if subcontractor)', type: 'text', width: 'half' },
    { id: 'occupation',        label: 'Occupation',                 type: 'text', width: 'half' },
    { id: 'topics',            label: 'Topics Covered',             type: 'checklist', required: true,
      options: ['Covered', 'N/A'],
      items: ['Site hazards overview', 'Emergency procedures & muster point', 'First aid location & attendants',
              'PPE requirements', 'FLHA / tailgate process', 'Incident & near-miss reporting',
              'Silica & dust exposure control plan', 'Compressed air & pressurized line safety',
              'Mobile equipment & spotter rules', 'Overhead & underground utilities',
              'Fall protection requirements', 'Disciplinary policy', 'Site-specific rules'] },
    { id: 'certs_verified',    label: 'Certifications Verified',    type: 'textarea', placeholder: 'e.g. first aid, hoisting, driver licence class...' },
    { id: 'muster_communicated', label: 'Emergency Muster Point Communicated', type: 'radio', options: ['Yes'], required: true },
    { id: 'questions_answered',  label: 'Worker Questions Answered',           type: 'radio', options: ['Yes'], required: true },
    { id: 'supervisor_name',   label: 'Orientation Given By',       type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Worker Sign Off',            type: 'signature' }
  ],

  'Daily Site Report': [
    { id: '_section_cond',     label: '🌤️ Conditions',             type: 'section' },
    { id: 'weather',           label: 'Weather',                    type: 'select', required: true, width: 'half',
      options: ['Clear', 'Cloudy', 'Rain', 'Snow', 'Fog', 'Extreme heat', 'Extreme cold'] },
    // min: null = any sign (number fields refuse negatives otherwise) — cold is negative.
    { id: 'temperature_c',     label: 'Temperature (°C)',           type: 'number', min: null, width: 'half' },
    { id: 'wind',              label: 'Wind',                       type: 'select', width: 'half',
      options: ['Calm', 'Moderate', 'High — work restricted'] },
    { id: 'site_conditions',   label: 'Site Conditions Notes',      type: 'text', placeholder: 'Ground, access, water...', width: 'half' },
    { id: '_section_crew',     label: '👷 Crew & Equipment',        type: 'section' },
    { id: 'crew',              label: 'Crew on Site',               type: 'table', required: true, maxRows: 20,
      columns: [ { key: 'name', label: 'Name', type: 'text' }, { key: 'role', label: 'Role', type: 'text' },
                 { key: 'hours', label: 'Hours', type: 'number' } ] },
    { id: 'equipment',         label: 'Equipment on Site',          type: 'table', maxRows: 12,
      columns: [ { key: 'equipment', label: 'Equipment', type: 'text' }, { key: 'id', label: 'ID', type: 'text' },
                 { key: 'hours', label: 'Hours used', type: 'number' } ] },
    { id: '_section_work',     label: '🚧 Work',                    type: 'section' },
    { id: 'work_performed',    label: 'Work Performed Today',       type: 'textarea', required: true },
    { id: 'production',        label: 'Production Quantities',      type: 'table', maxRows: 12,
      columns: [ { key: 'item', label: 'Item', type: 'text' }, { key: 'qty', label: 'Qty', type: 'number' },
                 { key: 'unit', label: 'Unit', type: 'text' } ] },
    { id: 'delays',            label: 'Delays / Standby',           type: 'textarea', placeholder: 'Cause and duration...' },
    { id: 'visitors',          label: 'Visitors / Deliveries',      type: 'textarea' },
    { id: 'safety_events',     label: 'Safety Events Today?',       type: 'radio', required: true,
      options: ['None', 'See incident report(s)'] },
    { id: 'photos',            label: 'Photos',                     type: 'photo' },
    { id: 'supervisor_name',   label: 'Foreman Name',               type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Foreman Sign Off',           type: 'signature' }
  ],

  'Anchor Installation Log': [
    { id: 'wall_area',         label: 'Wall / Area',                type: 'text', required: true, width: 'half', placeholder: 'e.g. East wall, row 2' },
    { id: 'drawing_ref',       label: 'Drawing Ref #',              type: 'text', width: 'half' },
    { id: 'element_type',      label: 'Element Type',               type: 'select', required: true, width: 'half',
      options: ['DCP anchor', 'Strand anchor', 'Soil nail', 'Micropile', 'Rock bolt / dowel', 'Other'] },
    { id: 'drill_method',      label: 'Drill Method',               type: 'select', width: 'half',
      options: ['Air track / percussive', 'Hydraulic rotary', 'Duplex / cased', 'Down-the-hole hammer', 'Other'] },
    { id: 'units',             label: 'Depth / Grout Units',        type: 'select', required: true, width: 'half',
      options: ['ft & bags', 'm & litres'] },
    { id: 'bar_type',          label: 'Bar / Strand Type & Size',   type: 'text', width: 'half', placeholder: 'Grade & diameter per spec' },
    { id: 'grout_mix',         label: 'Grout Mix / W:C Ratio',      type: 'text', width: 'half' },
    { id: 'holes',             label: 'Holes Installed',            type: 'table', required: true, maxRows: 40,
      columns: [ { key: 'hole', label: 'Hole #', type: 'text' }, { key: 'depth', label: 'Depth', type: 'number' },
                 { key: 'angle', label: 'Angle °', type: 'number' }, { key: 'grout', label: 'Grout take', type: 'number' },
                 { key: 'notes', label: 'Notes', type: 'text' } ] },
    { id: 'ground_conditions', label: 'Obstructions / Ground Conditions', type: 'textarea', placeholder: 'Caving, voids, water...' },
    { id: 'total_drilled',     label: 'Total Drilled Length Today', type: 'number', width: 'half' },
    { id: 'supervisor_name',   label: 'Driller Name',               type: 'text', required: true, width: 'half' },
    { id: 'signature',         label: 'Driller Sign Off',           type: 'signature' }
  ],

  'Anchor Test Record': [
    { id: 'anchor_id',         label: 'Anchor / Element ID',        type: 'text', required: true, width: 'half', placeholder: 'Matches installation log hole #' },
    { id: 'wall_area',         label: 'Wall / Area',                type: 'text', required: true, width: 'half' },
    { id: 'test_type',         label: 'Test Type',                  type: 'radio', required: true,
      options: ['Proof', 'Performance', 'Lift-off'] },
    { id: 'design_load',       label: 'Design Load (kN or kips)',   type: 'number', required: true, width: 'half' },
    { id: 'jack_gauge_id',     label: 'Jack / Gauge ID',            type: 'text', width: 'half', placeholder: 'Calibration traceability' },
    { id: 'load_steps',        label: 'Load Steps',                 type: 'table', required: true, maxRows: 15,
      columns: [ { key: 'load', label: 'Load', type: 'number' }, { key: 'hold', label: 'Hold (min)', type: 'number' },
                 { key: 'movement', label: 'Movement (mm)', type: 'number' }, { key: 'notes', label: 'Notes', type: 'text' } ] },
    { id: 'max_load_held',     label: 'Max Test Load Held',         type: 'number', required: true, width: 'half' },
    { id: 'lock_off_load',     label: 'Lock-Off Load',              type: 'number', width: 'half' },
    { id: 'acceptable',        label: 'Creep / Movement Acceptable Per Spec?', type: 'radio', required: true,
      options: ['Yes — accepted', 'No — see notes', 'Engineer to review'] },
    { id: 'engineer_present',  label: 'Engineer / Inspector Present', type: 'text', width: 'half' },
    { id: 'supervisor_name',   label: 'Tested By',                  type: 'text', required: true, width: 'half' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Shotcrete Placement Record': [
    { id: 'panel_id',          label: 'Wall / Panel / Lift ID',     type: 'text', required: true, width: 'half' },
    { id: 'process',           label: 'Process',                    type: 'radio', required: true, options: ['Wet-mix', 'Dry-mix'] },
    { id: 'mix_id',            label: 'Mix ID / Supplier Ticket #', type: 'text', required: true, width: 'half' },
    { id: 'batch_to_place',    label: 'Batch Time → Placement Time', type: 'text', width: 'half', placeholder: 'Age at placement' },
    { id: 'volume_m3',         label: 'Volume Placed (m³)',         type: 'number', required: true, width: 'half' },
    { id: 'design_thickness',  label: 'Design Thickness (mm)',      type: 'number', width: 'half' },
    { id: 'thickness_verified',label: 'Thickness Verified By',      type: 'select', width: 'half',
      options: ['Depth pins / ground wires', 'Probe', 'Drilled check', 'Other'] },
    { id: 'area_m2',           label: 'Area Placed (m²)',           type: 'number', width: 'half' },
    { id: 'reinforcement',     label: 'Reinforcement in Place',     type: 'radio', required: true,
      options: ['Wire mesh', 'Rebar', 'Fibre', 'Combination', 'None'] },
    { id: 'nozzle_operator',   label: 'Nozzle Operator',            type: 'text', required: true, width: 'half',
      placeholder: 'Certification per spec noted here' },
    { id: 'equipment_id',      label: 'Pump / Equipment ID',        type: 'text', width: 'half' },
    { id: 'ambient_temp',      label: 'Ambient Temp (°C)',          type: 'number', width: 'half' },
    { id: 'surface_prep',      label: 'Surface Prep Verified',      type: 'radio', required: true, options: ['Yes', 'No'] },
    { id: 'cure_method',       label: 'Cure Method',                type: 'select', width: 'half',
      options: ['Water', 'Curing compound', 'Blankets', 'None specified'] },
    { id: 'test_panels',       label: 'Test Panels Shot?',          type: 'radio', required: true,
      options: ['Yes — see Test Panel Log', 'Not required today'] },
    { id: 'rebound_notes',     label: 'Rebound / Trimming Notes',   type: 'textarea' },
    { id: 'photos',            label: 'Photos',                     type: 'photo' },
    { id: 'supervisor_name',   label: 'Recorded By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Shotcrete Test Panel Log': [
    { id: 'panel_id',          label: 'Panel ID',                   type: 'text', required: true, width: 'half' },
    { id: 'linked_placement',  label: 'Linked Placement (wall / panel ID)', type: 'text', required: true, width: 'half' },
    { id: 'date_shot',         label: 'Date Shot',                  type: 'date', required: true, width: 'half' },
    { id: 'nozzle_operator',   label: 'Nozzle Operator',            type: 'text', required: true, width: 'half' },
    { id: 'mix_id',            label: 'Mix ID',                     type: 'text', required: true, width: 'half' },
    { id: 'panel_size',        label: 'Panel Size & Orientation',   type: 'text', width: 'half', placeholder: 'Per spec' },
    { id: 'cores',             label: 'Cores',                      type: 'table', maxRows: 12,
      columns: [ { key: 'core', label: 'Core #', type: 'text' }, { key: 'age', label: 'Age (days)', type: 'number' },
                 { key: 'strength', label: 'Strength (MPa)', type: 'number' }, { key: 'notes', label: 'Grade / notes', type: 'text' } ] },
    { id: 'sent_to_lab',       label: 'Sent to Lab (date)',         type: 'date', width: 'half' },
    { id: 'lab_report',        label: 'Lab / Report #',             type: 'text', width: 'half' },
    { id: 'meets_spec',        label: 'Results Meet Spec?',         type: 'radio', options: ['Yes', 'No', 'Pending'] },
    { id: 'supervisor_name',   label: 'Recorded By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Grout Batch Log': [
    { id: 'equipment_id',      label: 'Grout Plant / Equipment ID', type: 'text', required: true, width: 'half' },
    { id: 'mix_design',        label: 'Mix Design / W:C Ratio',     type: 'text', required: true, width: 'half' },
    { id: 'batches',           label: 'Batches Today',              type: 'table', required: true, maxRows: 20,
      columns: [ { key: 'batch', label: 'Batch #', type: 'text' }, { key: 'time', label: 'Time', type: 'text' },
                 { key: 'water', label: 'Water (L)', type: 'number' }, { key: 'cement', label: 'Cement (bags)', type: 'number' },
                 { key: 'notes', label: 'Notes', type: 'text' } ] },
    { id: 'additives',         label: 'Additives',                  type: 'text', width: 'half' },
    { id: 'flow_test',         label: 'Fluidity / Flow Test Done?', type: 'radio', width: 'half', options: ['Yes', 'No'] },
    { id: 'cubes',             label: 'Cubes / Specimens Cast',     type: 'table', maxRows: 12,
      columns: [ { key: 'set', label: 'Set #', type: 'text' }, { key: 'element', label: 'For element', type: 'text' },
                 { key: 'age', label: 'Age (days)', type: 'number' }, { key: 'strength', label: 'Strength (MPa)', type: 'number' } ] },
    { id: 'notes',             label: 'Notes',                      type: 'textarea', placeholder: 'Flow values, temperatures, issues...' },
    { id: 'supervisor_name',   label: 'Recorded By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  // ── Hot Work & Fire Watch — neutral, every trade ────────────────────────────
  // Born as the roofing pack's torch record; 2026-09-03 (Renan) it became the
  // cross-trade form: masonry grinds and cuts, mechanical welds and brazes,
  // demolition torches, roofing still torches membrane. One form, neutral
  // words, multi-select for the work actually done + "Other" for the rest.
  // Lines that only apply to gas/torch crews are marked N/A by the others —
  // the checklist widget already has that button. Fire watch itself is
  // universal and unchanged (duration per the tenant's fire safety plan /
  // permit — no code citation in form text).
  'Hot Work & Fire Watch Record': [
    { id: 'work_area',          label: 'Work Area / Location',          type: 'text', placeholder: 'e.g. Level 2 east wall, Section B', required: true, width: 'half' },
    { id: 'permit_number',      label: 'Hot Work Permit # (if GC-issued)', type: 'text', width: 'half' },
    { id: 'hot_work_types',     label: 'Hot Work Performed',            type: 'checkbox-group', required: true,
      options: ['Cutting / grinding (sparks)', 'Welding', 'Torch / open flame', 'Soldering / brazing',
                'Heating / thawing', 'Hot asphalt (kettle)', 'Heat welding (membrane)', 'Other'] },
    { id: 'hot_work_other',     label: 'If other, describe',            type: 'text', width: 'half' },
    { id: 'combustibles',       label: 'Combustibles Nearby',           type: 'radio', required: true,
      options: ['None within 11 m (35 ft)', 'Moved or shielded before starting',
                'Fire-resistant covers / blankets in place', 'Work on a non-combustible surface only'] },
    { id: 'checklist',          label: 'Fire Prevention & Setup',       type: 'checklist', required: true,
      items: ['Hot work permit posted (if required)', 'Fire watch person assigned',
              'Fire extinguisher at the work area — checked & charged', 'Extinguisher within reach of the work',
              'Combustibles cleared or shielded — including below and behind', 'Sparks / slag contained (screens, blankets)',
              'Openings, cracks and floor drains covered', 'Gas cylinders secured upright — hoses & regulators checked',
              'Leak test performed on gas equipment', 'Fire alarm / sprinklers not impaired (or GC notified)'] },
    { id: 'extinguisher_count', label: 'Extinguishers at Work Area',    type: 'number', width: 'half' },
    { id: 'torches_out_time',   label: 'All Hot Work Stopped At',       type: 'text', placeholder: 'e.g. 15:30', required: true, width: 'half' },
    { id: 'fire_watch_start',   label: 'Fire Watch Start',              type: 'text', width: 'half' },
    { id: 'fire_watch_end',     label: 'Fire Watch End',                type: 'text', required: true, width: 'half',
      help: 'Duration per your fire safety plan / permit' },
    { id: 'hot_spots',          label: 'Hot Spots Found',               type: 'radio', required: true,
      options: ['None found', 'Hot spots found — cooled and rechecked'] },
    { id: 'ir_scan',            label: 'Infrared / Thermal Scan Completed', type: 'radio', options: ['Yes', 'No', 'N/A'] },
    { id: 'notes',              label: 'Notes',                         type: 'textarea', placeholder: 'Anything found during the watch...' },
    { id: 'supervisor_name',    label: 'Fire Watch By',                 type: 'text', required: true },
    { id: 'signature',          label: 'Sign Off',                      type: 'signature' }
  ],

  // ── Excavation & Sitework pack forms ────────────────────────────────────────
  // The daily record a competent person signs before anyone enters: soil type
  // and protection method chosen together, locate ticket carried on the form,
  // and a go / no-go determination. Provincial rules require the inspection
  // each shift and again after rain or vibration — the form records that it
  // happened; when it is required is the tenant's call with their advisor.
  'Excavation Daily Inspection': [
    { id: 'work_area',          label: 'Excavation / Location',      type: 'text', placeholder: 'e.g. Sanitary run, STA 1+40 to 1+90', required: true, width: 'half' },
    { id: 'depth_m',            label: 'Depth (m)',                  type: 'number', required: true, width: 'half' },
    { id: 'length_m',           label: 'Length (m)',                 type: 'number', width: 'half' },
    { id: 'soil_type',          label: 'Soil Type',                  type: 'radio', required: true,
      options: ['Type 1 — hard and very dense', 'Type 2 — dense, pick driven with effort', 'Type 3 — previously excavated or loose',
                'Type 4 — soft, wet or muddy', 'Solid rock'] },
    { id: 'protection_method',  label: 'Protection Method',          type: 'radio', required: true,
      options: ['Sloped / benched to spec', 'Trench box or shield', 'Shoring system', 'Solid rock — none required'] },
    { id: 'engineered_design',  label: 'Engineered Design on Site',  type: 'radio', width: 'half',
      options: ['Yes', 'No', 'N/A'] },
    { id: 'locate_ticket',      label: 'Locate Ticket #',            type: 'text', width: 'half' },
    { id: 'locates_valid',      label: 'Locates Valid & Marks Visible', type: 'radio', required: true,
      options: ['Yes', 'No — work stopped'] },
    { id: 'tolerance_zones',    label: 'Tolerance Zones Daylighted',  type: 'radio', required: true,
      options: ['Yes — hydrovac or hand', 'Not required today', 'No entry within zones'] },
    { id: 'checklist',          label: 'Excavation Inspection',      type: 'checklist', required: true,
      items: ['Walls and faces stable — no undercutting or tension cracks', 'Protection installed to spec and undamaged',
              'Spoil piles and materials set back from the edge', 'No water accumulating in the bottom',
              'Surface water diverted away from the edge', 'Ladder access within reach of workers',
              'Edges barricaded or fenced', 'Equipment kept back from the edge',
              'Vibration sources controlled', 'Adjacent structures and footings supported',
              'Exposed utilities supported and flagged', 'Atmosphere tested where required',
              'Weather since last inspection reviewed'] },
    { id: 'conditions_changed', label: 'Conditions Changed Since Last Inspection', type: 'radio', required: true,
      options: ['No change', 'Changed — reinspected before entry'] },
    { id: 'final_determination', label: 'Final Determination',       type: 'radio', required: true,
      options: ['Excavation is SAFE to enter as per this inspection', 'Excavation is UNSAFE — no entry until corrected'] },
    { id: 'deficiency_details', label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'supervisor_name',    label: 'Competent Person Name',      type: 'text', required: true },
    { id: 'signature',          label: 'Competent Person Sign Off',  type: 'signature' }
  ],

  // Same shape as the drilling pack's five equipment logs (one form a crew
  // learns once), generalized across the earthmoving fleet by equipment_type.
  'Heavy Equipment Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'equipment_type',    label: 'Equipment Type',             type: 'select', required: true, width: 'half',
      options: ['Excavator', 'Backhoe', 'Loader', 'Skid steer', 'Dozer', 'Grader', 'Roller / compactor', 'Dump truck', 'Other'] },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'serial_number',     label: 'Serial #',                   type: 'text',   width: 'half' },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'service_due_hours', label: 'Next Service Due (hours)',   type: 'number', width: 'half' },
    { id: 'checklist',         label: 'Pre-Operational Inspection', type: 'checklist', required: true,
      items: ['Engine oil level', 'Hydraulic oil level', 'Coolant level', 'Fuel level',
              'Leaks — oil, fuel, hydraulic, coolant', 'Hoses, lines & fittings condition',
              'Tracks / tires & undercarriage', 'Bucket, blade or drum condition',
              'Quick coupler engaged & pins secure', 'Attachment condition', 'Greasing points serviced',
              'Controls & gauges functional', 'Brakes & steering', 'Horn & backup alarm',
              'Lights & beacon', 'Mirrors & cameras clean', 'ROPS / FOPS & seatbelt condition',
              'Guards, covers & handrails in place', 'Fire extinguisher on board',
              'Housekeeping — cab and steps clear'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  // ── Mechanical pack forms (plumbing & HVAC) ─────────────────────────────────
  // The two records the trade owes someone else: the AHJ witnesses the
  // pressure test before a phase is released, and federal halocarbon rules
  // want the refrigerant log kept for years. Neither fits the QAQC overlay.

  'Pressure Test Record': [
    { id: 'system_tested',     label: 'System Tested',              type: 'radio', required: true,
      options: ['DWV — drain, waste & vent', 'Domestic water', 'Hydronic heating / chilled',
                'Natural gas / propane', 'Refrigerant piping', 'Fire protection piping'] },
    { id: 'location_area',     label: 'Area / Section',             type: 'text', required: true, width: 'half',
      placeholder: 'e.g. Level 3 east riser' },
    { id: 'drawing_ref',       label: 'Drawing Ref #',              type: 'text', width: 'half' },
    { id: 'test_medium',       label: 'Test Medium',                type: 'radio', required: true,
      options: ['Water', 'Air', 'Nitrogen', 'Other'] },
    { id: 'test_pressure',     label: 'Test Pressure',              type: 'number', required: true, width: 'half' },
    { id: 'pressure_unit',     label: 'Pressure Unit',              type: 'select', width: 'half',
      options: ['psi', 'kPa', 'bar'] },
    { id: 'required_duration', label: 'Required Duration (hours)',  type: 'number', width: 'half' },
    { id: 'start_time',        label: 'Test Start Time',            type: 'text', width: 'half', placeholder: 'e.g. 08:15' },
    { id: 'end_time',          label: 'Test End Time',              type: 'text', width: 'half', placeholder: 'e.g. 12:15' },
    { id: 'pressure_start',    label: 'Pressure at Start',          type: 'number', width: 'half' },
    { id: 'pressure_end',      label: 'Pressure at End',            type: 'number', width: 'half' },
    { id: 'result',            label: 'Result',                     type: 'radio', required: true,
      options: ['PASS — held per spec', 'FAIL — leak found, repair and retest required'] },
    { id: 'leak_notes',        label: 'Leak Location / Repair Notes', type: 'textarea',
      placeholder: 'Where it leaked and what was done...' },
    { id: 'witnessed_by',      label: 'Witnessed By',               type: 'text',
      placeholder: 'Inspector, consultant or GC representative on site' },
    { id: 'supervisor_name',   label: 'Tested By',                  type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Refrigerant Service & Leak Log': [
    { id: 'equipment_id',      label: 'Equipment / System ID',      type: 'text', required: true, width: 'half',
      placeholder: 'Your unit number' },
    { id: 'equipment_type',    label: 'Equipment Type',             type: 'select', width: 'half',
      options: ['Rooftop unit', 'Split system', 'Chiller', 'Heat pump', 'Walk-in / refrigeration', 'Other'] },
    { id: 'refrigerant_type',  label: 'Refrigerant Type',           type: 'text', required: true, width: 'half',
      placeholder: 'As per nameplate' },
    { id: 'service_type',      label: 'Service Type',               type: 'radio', required: true,
      options: ['Charging', 'Recovery', 'Leak check', 'Repair', 'Decommission'] },
    { id: 'amount_added',      label: 'Amount Added (kg)',          type: 'number', width: 'half' },
    { id: 'amount_recovered',  label: 'Amount Recovered (kg)',      type: 'number', width: 'half' },
    { id: 'cylinder_id',       label: 'Cylinder ID',                type: 'text', width: 'half' },
    { id: 'leak_test_method',  label: 'Leak Test Method',           type: 'select', width: 'half',
      options: ['Electronic detector', 'Bubble solution', 'Nitrogen pressure test', 'UV dye', 'None this visit'] },
    { id: 'leak_found',        label: 'Leak Found',                 type: 'radio', required: true,
      options: ['No leak detected', 'Leak found — repaired', 'Leak found — repair scheduled'] },
    { id: 'leak_location',     label: 'Leak Location',              type: 'text', placeholder: 'Component and location on the system' },
    { id: 'repair_description',label: 'Repair Description',         type: 'textarea' },
    { id: 'returned_to_service', label: 'System Returned to Service', type: 'radio', required: true,
      options: ['Yes — returned to service', 'No — system down'] },
    { id: 'technician_cert',   label: 'Technician Certification #',  type: 'text', width: 'half',
      help: 'Only a certified technician may handle refrigerant' },
    { id: 'supervisor_name',   label: 'Recorded By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  // ── MEWP Daily Log (neutral) ────────────────────────────────────────────────
  // Same canonical equipment block as the other daily logs, plus what makes a
  // lift different: the WORKPLACE is inspected alongside the machine (ground,
  // overhead lines, wind), because that is what tips one over.
  'MEWP Daily Log': [
    { id: 'equipment_id',      label: 'Equipment ID',               type: 'text',   placeholder: 'Your unit number', required: true, width: 'half' },
    { id: 'make_model',        label: 'Make / Model',               type: 'text',   width: 'half' },
    { id: 'mewp_type',         label: 'MEWP Type',                  type: 'select', required: true, width: 'half',
      options: ['Scissor lift', 'Articulating boom', 'Telescopic boom', 'Vertical mast lift', 'Trailer-mounted boom', 'Other'] },
    { id: 'ownership',         label: 'Ownership',                  type: 'select', options: ['Owned', 'Rented'], width: 'half' },
    { id: 'rental_company',    label: 'Rental Company (if rented)', type: 'text',   width: 'half' },
    { id: 'hour_meter',        label: 'Hour Meter Reading',         type: 'number', required: true, width: 'half' },
    { id: 'annual_inspection', label: 'Annual Inspection Date',     type: 'date',   width: 'half',
      help: 'From the record that came with the machine' },
    { id: 'checklist',         label: 'Pre-Use Inspection',         type: 'checklist', required: true,
      items: ['Operator manual in the machine', 'Decals, placards & load capacity legible',
              'Guardrails, mid-rails & toe boards secure', 'Entry gate closes and latches',
              'Fall protection anchor points intact', 'Ground controls functional',
              'Platform controls functional', 'Emergency stop functional at both stations',
              'Emergency lowering tested', 'Horn & alarms working',
              'Tilt / pothole protection functional', 'Outriggers, stabilizers & levelling',
              'Tires, tracks & wheels condition', 'Hydraulic hoses & cylinders — no leaks',
              'Battery charge / fuel level & connections', 'Guards, covers & pins in place',
              'Ground conditions — slope, holes, soft ground, floor capacity',
              'Overhead obstructions & power line clearance', 'Wind & weather within the machine limits',
              'Traffic & pedestrians controlled around the work area',
              'Annual inspection current and records available'] },
    { id: 'deficiency_details',  label: 'Deficiency Details / Notes', type: 'textarea', placeholder: 'Anything found above — and what was done about it...' },
    { id: 'final_determination', label: 'Final Determination',        type: 'radio',
      options: ['Equipment is SAFE for use as per today\'s inspection', 'Equipment REQUIRES REPAIR before use — do not operate'], required: true },
    { id: 'supervisor_name',     label: 'Operator Name',              type: 'text', required: true },
    { id: 'signature',           label: 'Operator Sign Off',          type: 'signature' }
  ],

  // ── Demolition & Environmental pack forms ───────────────────────────────────
  // Two gates the law puts in front of this trade: nothing starts until the
  // hazardous materials survey is in hand and every utility is proven dead,
  // and an abatement enclosure is checked before AND during each shift.
  // Risk wording is Low / Moderate / High on purpose — provinces name the
  // classes differently, and that mapping belongs in the docs, not the form.

  'Pre-Demolition Readiness Check': [
    { id: 'structure_area',    label: 'Structure / Area',           type: 'text', required: true, width: 'half',
      placeholder: 'e.g. Building B, west wing' },
    { id: 'work_type',         label: 'Work Type',                  type: 'radio', required: true,
      options: ['Interior strip-out', 'Selective demolition', 'Full structural demolition', 'Abatement only'] },
    { id: 'survey_status',     label: 'Hazardous Materials Survey', type: 'radio', required: true,
      options: ['Received and reviewed', 'Pending — no work until received', 'Not required per owner (documented)'] },
    { id: 'survey_ref',        label: 'Survey / Report #',          type: 'text', width: 'half' },
    { id: 'materials_found',   label: 'Hazardous Materials Identified', type: 'checkbox-group',
      options: ['Asbestos', 'Lead', 'Silica', 'Mercury', 'PCBs', 'Mould', 'Other', 'None identified'] },
    { id: 'checklist',         label: 'Readiness Check',            type: 'checklist', required: true,
      items: ['Survey reviewed with the crew', 'Hazardous materials list posted on site',
              'Electrical disconnected and verified de-energized', 'Gas disconnected and capped by the utility',
              'Water and sewer capped', 'Overhead lines identified or de-energized',
              'Underground services located', 'Demolition permit and notices posted',
              'Demolition sequence on site', 'Temporary supports installed per plan',
              'Structures to be retained protected', 'Exclusion zone barricaded and signed',
              'Emergency access and muster point confirmed'] },
    { id: 'utility_ref',       label: 'Utility Disconnection Reference / Permit #', type: 'text' },
    { id: 'engineer_of_record',label: 'Engineer of Record',         type: 'text', width: 'half' },
    { id: 'determination',     label: 'Determination',              type: 'radio', required: true,
      options: ['READY — demolition may proceed', 'NOT READY — work held until items corrected'] },
    { id: 'notes',             label: 'Notes',                      type: 'textarea',
      placeholder: 'Anything outstanding and who is closing it...' },
    { id: 'supervisor_name',   label: 'Verified By',                type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ],

  'Abatement Containment Daily Log': [
    { id: 'work_area',         label: 'Work Area',                  type: 'text', required: true, width: 'half',
      placeholder: 'e.g. Level 2 mechanical room' },
    { id: 'risk_class',        label: 'Risk Classification',        type: 'radio', required: true,
      options: ['Low risk', 'Moderate risk', 'High risk', 'Other hazardous material'] },
    { id: 'material_type',     label: 'Material Type',              type: 'select', width: 'half',
      options: ['Asbestos', 'Lead', 'Mould', 'Mercury', 'Other'] },
    { id: 'shift_start',       label: 'Shift Start',                type: 'text', width: 'half', placeholder: 'e.g. 07:00' },
    { id: 'shift_end',         label: 'Shift End',                  type: 'text', width: 'half' },
    { id: 'pressure_reading',  label: 'Negative Pressure Reading',  type: 'number', width: 'half',
      help: 'Record what the enclosure gauge shows' },
    { id: 'pressure_unit',     label: 'Reading Unit',               type: 'select', width: 'half',
      options: ['in w.c.', 'Pa'] },
    { id: 'checklist',         label: 'Containment Check',          type: 'checklist', required: true,
      items: ['Enclosure barriers intact — no tears or gaps', 'Critical barriers sealed (doors, windows, vents)',
              'Negative air machines running and exhausting correctly', 'HEPA filters and pre-filters checked',
              'Manometer functioning and reading recorded', 'Decontamination unit set up and clean',
              'Shower operating with filtered discharge', 'Workers in correct respirators and coveralls',
              'Waste double-bagged and labelled before leaving the enclosure', 'Waste storage area secure',
              'Warning signs posted at every access point', 'Wet methods in use'] },
    { id: 'air_monitoring',    label: 'Air Monitoring',             type: 'radio',
      options: ['Not required today', 'Occupational monitoring in progress', 'Clearance sampling requested'] },
    { id: 'workers_inside',    label: 'Workers in Enclosure',       type: 'number', width: 'half' },
    { id: 'manifest_ref',      label: 'Waste Manifest #',           type: 'text', width: 'half' },
    { id: 'deficiency_details',label: 'Deficiency Details / Notes', type: 'textarea',
      placeholder: 'Anything found above — and what was done about it...' },
    { id: 'determination',     label: 'Determination',              type: 'radio', required: true,
      options: ['Containment SOUND — work continues', 'Containment BREACHED — work stopped and area secured'] },
    { id: 'supervisor_name',   label: 'Supervisor Name',            type: 'text', required: true, autofill: 'foreman' },
    { id: 'signature',         label: 'Sign Off',                   type: 'signature' }
  ]
};

// ═════════════════════════════════════════════════════════════════════════════
// TRADE PACKS — conteúdo por trade é resolvido por render (metaFor/fieldsFor/
// visibleSubmissionTypes, via window.Ordinex.forms.resolve/packs.visibleTypes),
// a partir de form_schemas org-scoped por pack_key (Fase 2, Tasks 2/3). O
// overlay in-code que mutava TAILGATE_ITEMS/DEFAULT_FLRA_TASKS/TOOLBOX_HAZARDS/
// QAQC_ITEMS morreu nesta task — esses arrays seguem só como fallback neutro
// (nenhuma linha org / boot frio offline). applyTradePacks(), mais abaixo, faz
// apenas o [data-pack] toggling de UI a cada render (spec §5.3/D6).
// ═════════════════════════════════════════════════════════════════════════════

// Packs the current org opted into (empty until window.currentOrg resolves).
// Driven purely by the org's trade_pack_<key> feature flags — every pack
// (masonry, drilling_shotcrete…) gates its SUBMISSION_TYPES entries through
// here; content always overlays from org-scoped form_schemas rows, never
// from an in-code map.
function activeTradePacks() {
  const feats = (window.currentOrg && window.currentOrg.features) || {};
  return Object.keys(feats)
    .filter(k => k.indexOf('trade_pack_') === 0 && feats[k] === true)
    .map(k => k.slice('trade_pack_'.length));
}

// Escolha manual do foreman (seletor de trade na tela Hoje). Só existe em orgs
// com 2+ packs; guardada por usuário para sobreviver a reload e ao dia
// seguinte, como o projeto ativo. Chave por usuário (não por org): o contexto
// da org carrega depois do primeiro render.
function tradeChoiceKey() {
  return 'ordinex_active_trade_' + ((window.currentUser && window.currentUser.id) || 'default');
}
function getTradeChoice() {
  try { return localStorage.getItem(tradeChoiceKey()) || ''; } catch (_) { return ''; }
}
function setTradeChoice(trade) {
  try {
    if (trade) localStorage.setItem(tradeChoiceKey(), trade);
    else localStorage.removeItem(tradeChoiceKey());
  } catch (_) { /* private mode */ }
}

// Trade "ativo" agora, em ordem de confiança:
//   1. a etiqueta do projeto (dado do admin, o mais confiável);
//   2. a escolha manual do foreman — só vale em org multi-pack, onde antes
//      disto o app caía no neutro em silêncio (auditoria 2026-07-29);
//   3. o único pack da org;
//   4. null (geral).
// Fonte única do contexto — TODO conteúdo por trade (metaFor/fieldsFor/grid/
// delivery/offline/registro da submission) deriva daqui, a cada render.
function activeTradePack() {
  try {
    const project = (typeof state !== 'undefined' && state.project) || '';
    const feats = (window.currentOrg && window.currentOrg.features) || {};
    if (!window.Ordinex?.packs) return null;
    const byProject = window.Ordinex.packs.tradeForProject(project, window.projectTrades, feats);
    // Etiqueta do projeto ganha: só cai na escolha manual quando o projeto não
    // tem etiqueta (tradeForProject devolve null com 2+ packs nesse caso).
    if (byProject) return byProject;
    const active = window.Ordinex.packs.active(feats);
    const chosen = getTradeChoice();
    return (chosen && active.includes(chosen)) ? chosen : null;
  } catch (_) { return null; }
}

// The form list the current org should see: pack-only forms filtered out,
// plus the org's own custom forms (Fase B FB3 — published org schemas
// flagged meta.custom, e.g. from the admin Forms builder).
function visibleSubmissionTypes() {
  const feats = (window.currentOrg && window.currentOrg.features) || {};
  const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;
  let custom = [];
  try { custom = window.Ordinex?.forms?.custom() || []; } catch (_) { /* boot order */ }
  // Custom forms go through the same filter, so the admin's "Show to crews"
  // switch (features.pack_hidden) hides them too.
  return window.Ordinex?.packs
    ? window.Ordinex.packs.visibleTypes([...SUBMISSION_TYPES, ...custom], { features: feats, trade })
    : [...SUBMISSION_TYPES.filter(t => !t.pack), ...custom];   // boot frio sem bundle: só neutros
}

// Só liga/desliga UI [data-pack] pela união dos packs ativos. Conteúdo por
// trade é derivado por render (metaFor/fieldsFor/visibleSubmissionTypes) —
// nada de mutação global (spec §5.3/D6).
function applyTradePacks() {
  const active = activeTradePacks();
  // Workers don't get Extra tools (the only [data-pack] UI). The rule lives
  // HERE because this re-runs after org.load, on the 60s revalidate and on a
  // trade change — a rule anywhere else gets undone.
  const worker = window.currentProfile?.role === 'worker';
  document.querySelectorAll('[data-pack]').forEach(el => {
    el.hidden = !active.includes(el.dataset.pack) || (worker && el.dataset.pack === 'masonry');
  });
}
